import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { VoiceChatProvider, useVoiceChatContext } from '../../contexts/VoiceChatContext';
import { useReportPolling } from '../../hooks/useReportPolling';
import apiService from '../../services/apiService';
import RoleplayTopBar from './RoleplayTopBar';
import RoleplayConversationArea from './RoleplayConversationArea';
import RoleplayPinnedPanel from './RoleplayPinnedPanel';
import RoleplayInputArea from './RoleplayInputArea';
import RoleplayScriptPanel from './RoleplayScriptPanel';
import ImplementationPanel from './ImplementationPanel';
import KnowledgeSituationPanel from './KnowledgeSituationPanel';
import RoleplayPrintDocument from './RoleplayPrintDocument';

import '../../styles/roleplay.css';

// ── Phase constants ────────────────────────────────────────────────────────────
const PHASE_TO_INDEX = { engage: 1, agree: 2, lead: 3, roleplay_debrief: 4 };
const phaseIndex = (phase) => PHASE_TO_INDEX[phase] ?? 0;

// Subphases handled by the roleplay session UI
const ROLEPLAY_SUBPHASES = new Set([
  'engage', 'agree', 'lead', 'roleplay_debrief',
]);

// ── Message ID factory ────────────────────────────────────────────────────────
let _msgId = 0;
const nextId = () => `rp-${++_msgId}-${Date.now()}`;

// ── Flat message list → display messages ─────────────────────────────────────
const messagesToDisplay = (allMessages, characterName, characterRole, sessionEnded = false) => {
  const display = [];

  for (let i = 0; i < allMessages.length; i++) {
    const m = allMessages[i];
    if (m.role === 'character') {
      const hasUserResponse = allMessages.slice(i + 1).some(n => n.role === 'user');
      if (m.coach_action === 'open') {
        // Show the opening line only once a user message follows it — avoids
        // duplicating it with the live addMessages call on the first advance.
        if (hasUserResponse) {
          display.push({ id: nextId(), type: 'character', text: m.content, characterName, characterRole });
        }
        continue;
      }
      if (!hasUserResponse) continue;
      display.push({
        id: nextId(),
        type: 'character',
        text: m.content,
        characterName,
        characterRole,
      });
    } else {
      display.push({
        id: nextId(),
        type: m.promoted ? 'client-promoted' : 'client-failed',
        text: m.content,
      });

      // The session-ending turn's character response has no following user
      // reply, so the branch above never displays it. Show it here, ahead of
      // this turn's coaching feedback, and skip its own iteration below.
      const nextMsg = allMessages[i + 1];
      const isClosingTurn = sessionEnded && nextMsg?.role === 'character' &&
        nextMsg.turn_id === m.turn_id && i + 1 === allMessages.length - 1;
      if (isClosingTurn) {
        display.push({ id: nextId(), type: 'character', text: nextMsg.content, characterName, characterRole });
      }

      if (m.coaching_feedback) {
        display.push({ id: nextId(), type: 'scarlett', text: m.coaching_feedback });
      }
      if (m.model_response) {
        const modelText = m.model_explanation
          ? `${m.model_response}\n\n${m.model_explanation}`
          : m.model_response;
        display.push({ id: nextId(), type: 'scarlett', text: modelText, label: 'Model response' });
      }
      if (isClosingTurn) i++;
    }
  }

  return display;
};

// ── Golden transcript → script exchanges ─────────────────────────────────────
const transcriptToExchanges = (goldenTranscript) => {
  const exchanges = [];
  let pendingChar = null;
  let pendingUser = null;

  for (const m of (goldenTranscript || [])) {
    if (m.coach_action === 'open') {
      pendingChar = m.content;
    } else if (m.role === 'character') {
      if (pendingUser !== null) {
        exchanges.push({ clientText: pendingUser, characterText: m.content });
        pendingUser = null;
      } else {
        pendingChar = m.content;
      }
    } else if (m.role === 'user') {
      if (pendingChar !== null) {
        exchanges.push({ clientText: m.content, characterText: pendingChar });
        pendingChar = null;
      } else {
        pendingUser = m.content;
      }
    }
  }

  // Flush any trailing unmatched message (e.g. character's last utterance with no reply yet)
  // Omit the absent side rather than setting null — PropTypes.string rejects null but allows undefined.
  if (pendingChar !== null) exchanges.push({ characterText: pendingChar });
  if (pendingUser !== null) exchanges.push({ clientText: pendingUser });

  return exchanges;
};

// ── Partial fill: 0.15 per promoted user turn in current phase, cap 0.7 ──────
const computePartialFill = (allMessages, currentPhase) => {
  const promotedInPhase = allMessages.filter(
    (m) => m.role === 'user' && m.phase === currentPhase && m.promoted
  ).length;
  return Math.min(0.7, promotedInPhase * 0.15);
};

// ── RoleplayViewInner ─────────────────────────────────────────────────────────
const RoleplayViewInner = ({ conversationId, conversation, conversationMessages, onTitleUpdate }) => {
  const { user } = useAuth();
  const voiceChat = useVoiceChatContext();
  const { showToast } = useToast();

  useEffect(() => {
    voiceChat.connect();
  }, [conversationId]); 

  // ── Phase routing state (initialized from conversation prop) ──────────────
  const [currentPhase, setCurrentPhase]       = useState(() => conversation?.currentPhase || 'knowledge');
  const [currentSubphase, setCurrentSubphase] = useState(() => conversation?.currentSubphase || 'situation');

  // ── Chat mode state (knowledge / scenario_creation / post-roleplay phases) ─
  const [chatMessages, setChatMessages]   = useState([]);
  const [isChatSending, setIsChatSending] = useState(false);
  const chatBottomRef                     = useRef(null);

  // ── Roleplay session state ────────────────────────────────────────────────
  const [loading, setLoading]             = useState(true);
  const [hydrationError, setHydrationError] = useState(null);
  const [sessionId, setSessionId]         = useState(null);
  const [sessionNum, setSessionNum]       = useState(1);
  const [characterName, setCharacterName] = useState(
    () => conversation?.character?.name || conversation?.caseStudy?.characterBrief?.name?.content || 'Character'
  );
  const [characterRole, setCharacterRole] = useState(
    () => conversation?.character?.role || conversation?.caseStudy?.characterBrief?.role?.content || ''
  );

  const [activePhase, setActivePhase]     = useState(
    () => conversation?.currentPhase === 'implementation' ? 4 : 0
  );
  const [phaseStatus, setPhaseStatus]     = useState({ engage: 'pending', agree: 'pending', lead: 'pending' });
  const [partialFill, setPartialFill]     = useState(0);

  const [messages, setMessages]           = useState([]);
  const [newMessageIds, setNewMessageIds] = useState(new Set());

  const [pinnedUtterance, setPinnedUtterance]   = useState('');
  const [pinnedMode, setPinnedMode]             = useState('active');
  const [pendingCharUtterance, setPendingChar]  = useState(null);

  const [scriptExchanges, setScriptExchanges]   = useState([]);
  const [scriptOpen, setScriptOpen]             = useState(false);
  const [scriptActiveTab, setScriptActiveTab]   = useState('situation');
  const [implementPanelOpen, setImplementPanelOpen] = useState(true);

  const [isSending, setIsSending]         = useState(false);

  const [caseStudy, setCaseStudy]         = useState(conversation?.caseStudy || null);

  const [debriefSessionId, setDebriefSessionId] = useState(null);
  const [debriefReport, setDebriefReport]       = useState(null);

  const clearNewMsgTimeout = useRef(null);
  const prevDebriefStatusRef = useRef(null);

  // ── Computed ──────────────────────────────────────────────────────────────
  const inRoleplayMode = currentPhase === 'practice' && ROLEPLAY_SUBPHASES.has(currentSubphase);
  const inImplementationMode = currentPhase === 'implementation';
  const inKnowledgeJustification = currentPhase === 'knowledge' &&
    (currentSubphase === 'justification' || currentSubphase === 'justification_confirmation');

  const inDebriefMode = currentSubphase === 'roleplay_debrief';
  const inputPlaceholder = (() => {
    if (currentPhase === 'knowledge' || currentSubphase === 'scenario_creation') {
      return 'Type here or click on the microphone to respond';
    }
    if (currentSubphase === 'roleplay_debrief') {
      if (debriefReport?.status === 'completed') return 'Your thoughts...';
      return 'When the report is done, we can reflect on the role play.';
    }
    return 'Your response';
  })();
  const inDebriefModeRef = useRef(false);
  inDebriefModeRef.current = inDebriefMode;

  // Keep a ref so handleChatSend (useCallback) always sees the current value
  const inKnowledgeJustificationRef = useRef(inKnowledgeJustification);
  inKnowledgeJustificationRef.current = inKnowledgeJustification;

  // ── Fetch fresh caseStudy when entering justification subphase ───────────
  useEffect(() => {
    if (!inKnowledgeJustification || !conversationId || !user) return;
    apiService.get(`/api/v1/conversations/${conversationId}/context`, user)
      .then(resp => {
        const freshCs = resp?.data?.caseStudy;
        if (freshCs && Object.keys(freshCs).length > 0) setCaseStudy(freshCs);
      })
      .catch(() => {});
  }, [inKnowledgeJustification, conversationId, user]);

  // ── Auto-scroll for chat mode ─────────────────────────────────────────────
  useEffect(() => {
    if (!inRoleplayMode) {
      chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, inRoleplayMode]);

  // ── Debrief polling ───────────────────────────────────────────────────────
  // A genuine 404 fires only when the session id itself doesn't exist. A
  // session that exists but never entered debriefing (or has no report and
  // isn't debriefing) comes back as 200 { status: 'not_found' } — also
  // terminal, so it's mapped to 'failed' here too, matching the hook's
  // completed/failed termination condition (otherwise it would poll silently
  // until the 4-minute timeout instead of stopping immediately).
  // Fetch the debrief body once, reusing the same abort signal as the status
  // poll it's chained off of. Also used standalone (no signal) by the manual
  // retry affordance and by the page-reload hydration path below.
  const fetchDebriefBody = useCallback(async (signal) => {
    const data = await apiService.get(
      `/api/v1/analytics/debriefs/by-session/${debriefSessionId}/body`,
      user,
      { signal }
    );
    return data?.data ?? null;
  }, [debriefSessionId, user]);

  const fetchDebriefStatus = useCallback(async (signal) => {
    try {
      const data = await apiService.get(
        `/api/v1/analytics/debriefs/by-session/${debriefSessionId}`,
        user,
        { signal }
      );
      const result = data?.data ?? null;
      if (result?.status === 'not_found') return { status: 'failed', reason: 'not_found' };
      if (result?.status === 'completed') {
        // Fire the body fetch once, on the transition into completed —
        // polling has already stopped by the time this resolves. The body
        // response is itself the full report object (status included).
        try {
          const body = await fetchDebriefBody(signal);
          return body ?? { status: 'completed', bodyFetchFailed: true };
        } catch (bodyErr) {
          if (bodyErr?.name === 'AbortError') return null;
          console.error('[RoleplayView] Debrief body fetch failed:', bodyErr);
          return { status: 'completed', bodyFetchFailed: true };
        }
      }
      return result;
    } catch (err) {
      if (err?.name === 'AbortError') return null;
      if (err?.status === 404) return { status: 'failed', reason: 'not_found' };
      return null;
    }
  }, [debriefSessionId, user, fetchDebriefBody]);

  const retryDebriefBody = useCallback(async () => {
    try {
      const body = await fetchDebriefBody();
      setDebriefReport(body ?? { status: 'completed', bodyFetchFailed: true });
    } catch (err) {
      console.error('[RoleplayView] Debrief body retry failed:', err);
      setDebriefReport((prev) => ({ ...prev, status: 'completed', bodyFetchFailed: true }));
    }
  }, [fetchDebriefBody]);

  useReportPolling({
    active: currentSubphase === 'roleplay_debrief' && !!debriefSessionId,
    pollKey: debriefSessionId,
    fetchStatus: fetchDebriefStatus,
    onUpdate: setDebriefReport,
  });

  // ── Toast when debrief report becomes ready ───────────────────────────────
  useEffect(() => {
    const status = debriefReport?.status;
    if (status === 'completed' && prevDebriefStatusRef.current !== 'completed') {
      showToast('Your debrief report is ready — check the panel on the right.', 'success');
    }
    prevDebriefStatusRef.current = status ?? null;
  }, [debriefReport?.status, showToast]);

  // ── Add roleplay messages, marking them as new ────────────────────────────
  const addMessages = useCallback((newMsgs) => {
    const ids = new Set(newMsgs.map((m) => m.id));
    setMessages((prev) => [...prev, ...newMsgs]);
    setNewMessageIds(ids);
    if (clearNewMsgTimeout.current) clearTimeout(clearNewMsgTimeout.current);
    clearNewMsgTimeout.current = setTimeout(() => {
      setNewMessageIds(new Set());
      clearNewMsgTimeout.current = null;
    }, 700);
  }, []);

  // ── Roleplay session hydration (used on mount and on roleplay_ready transition) ──
  const hydrateRoleplaySession = useCallback(async (preSeededPinned = '', autoSpeakOpening = false) => {
    setLoading(true);
    try {
      const sessionsResp = await apiService.get(
        `/api/v1/roleplay/sessions?conversation_id=${conversationId}`,
        user
      );
      const sessions = sessionsResp?.data?.sessions || sessionsResp?.sessions || [];
      const activeSess = sessions.find((s) => s.status === 'active' || s.status === 'debriefing' || s.status === 'completed');

      if (!activeSess) {
        try {
          const convResp = await apiService.get(`/api/v1/conversations/${conversationId}/context`, user);
          const convData = convResp?.data?.conversation || (convResp?.data?.id ? convResp.data : null);
          const newPhase    = convData?.currentPhase    || 'knowledge';
          const newSubphase = convData?.currentSubphase || 'collection';

          if (newPhase === 'practice' && ROLEPLAY_SUBPHASES.has(newSubphase)) {
            // Session expected but missing — data error, do not auto-create.
            setHydrationError('No roleplay session found for this conversation. Please contact support.');
          } else {
            setCurrentPhase(newPhase);
            setCurrentSubphase(newSubphase);
          }
        } catch {
          setCurrentPhase('knowledge');
          setCurrentSubphase('situation');
        }
        setLoading(false);
        return;
      }

      setSessionId(activeSess.id);
      setSessionNum(sessions.length);

      const sessResp = await apiService.get(
        `/api/v1/roleplay/sessions/${activeSess.id}`,
        user
      );
      const sessData = sessResp?.data || sessResp;
      const session  = sessData?.session || activeSess;
      const goldenTx = sessData?.golden_transcript || [];

      const charName = conversation?.character?.name || conversation?.caseStudy?.characterBrief?.name?.content || 'Character';
      const charRole = conversation?.character?.role || conversation?.caseStudy?.characterBrief?.role?.content || '';
      setCharacterName(charName);
      setCharacterRole(charRole);

      const sessionEnded = session.status === 'debriefing' || session.status === 'completed';
      const phaseForIndex = sessionEnded
        ? 'roleplay_debrief'
        : (session.current_phase || session.currentPhase);
      setActivePhase(phaseIndex(phaseForIndex));
      setPhaseStatus(session.phase_status || { engage: 'pending', agree: 'pending', lead: 'pending' });
      const hydratedExchanges = transcriptToExchanges(goldenTx);
      // console.log('[RoleplayView] hydration — goldenTx length:', goldenTx.length, '| hydratedExchanges:', hydratedExchanges.length);
      setScriptExchanges(hydratedExchanges);

      const histResp = await apiService.get(
        `/api/v1/roleplay/sessions/${activeSess.id}/history`,
        user
      );
      const allMessages = histResp?.data?.messages || histResp?.messages || [];

      setPartialFill(computePartialFill(allMessages, session.current_phase || session.currentPhase));

      const displayMsgs = messagesToDisplay(allMessages, charName, charRole, sessionEnded);
      setMessages(displayMsgs);

      // Pinned panel: last promoted character message, falling back to opening line
      const promotedCharMsgs = goldenTx.filter(
        m => m.role === 'character' && m.coach_action !== 'open'
      );
      let pinnedText = promotedCharMsgs.length > 0
        ? promotedCharMsgs[promotedCharMsgs.length - 1].content
        : '';

      if (!pinnedText) {
        const openMsg = goldenTx.find(m => m.coach_action === 'open');
        pinnedText = openMsg?.content || preSeededPinned || '';
      }

      if (activeSess.status === 'debriefing' || activeSess.status === 'completed') {
        setCurrentSubphase('roleplay_debrief');
        setDebriefSessionId(activeSess.id);
        try {
          const reportData = await apiService.get(
            `/api/v1/analytics/debriefs/by-session/${activeSess.id}`,
            user
          );
          if (reportData?.data?.status === 'completed') {
            // Status no longer carries the body — a page reload landing on
            // an already-completed debrief needs the separate body fetch,
            // same as the live-polling completion path above.
            try {
              const bodyResp = await apiService.get(
                `/api/v1/analytics/debriefs/by-session/${activeSess.id}/body`,
                user
              );
              const body = bodyResp?.data ?? { status: 'completed', bodyFetchFailed: true };
              setDebriefReport(body);
              prevDebriefStatusRef.current = body.status ?? null;
            } catch (bodyErr) {
              console.error('[hydrate] debrief body fetch failed:', bodyErr?.status, bodyErr);
              setDebriefReport({ status: 'completed', bodyFetchFailed: true });
              prevDebriefStatusRef.current = 'completed';
            }
          } else if (reportData?.data) {
            setDebriefReport(reportData.data);
            prevDebriefStatusRef.current = reportData.data.status ?? null;
          }
        } catch (err) {
          console.error('[hydrate] debrief fetch failed:', err?.status, err);
        }

        // Append post-roleplay debrief conversation messages
        const lastRoleplayMsg = allMessages[allMessages.length - 1];
        if (lastRoleplayMsg?.created_at) {
          try {
            const convMsgsResp = await apiService.get(
              `/api/v1/conversations/${conversationId}/messages?after=${encodeURIComponent(lastRoleplayMsg.created_at)}`,
              user
            );
            const convMsgs = convMsgsResp?.data?.messages || [];
            const debriefDisplayMsgs = convMsgs.map(m => ({
              id: nextId(),
              type: m.sender === 'ai' ? 'scarlett' : 'user',
              text: m.content,
            }));
            if (debriefDisplayMsgs.length > 0) {
              setMessages(prev => [...prev, ...debriefDisplayMsgs]);
            }
          } catch {
            // non-fatal — debrief history just won't show prior chat messages
          }
        }
      } else if (pinnedText) {
        setPinnedUtterance(pinnedText);
        setPinnedMode('active');
        setPendingChar(pinnedText);
        if (autoSpeakOpening && voiceChat?.generateSpeech) {
          voiceChat.generateSpeech(pinnedText, 'character');
        }
      }
      try {
        const ctxResp = await apiService.get(`/api/v1/conversations/${conversationId}/context`, user);
        const freshCs = ctxResp?.data?.caseStudy;
        if (freshCs && Object.keys(freshCs).length > 0) setCaseStudy(freshCs);
      } catch {
        // non-fatal — fall back to prop value
      }
    } catch (err) {
      console.error('RoleplayView session hydration error:', err);
      setCurrentPhase('knowledge');
      setCurrentSubphase('situation');
    } finally {
      setLoading(false);
    }
  }, [conversationId, user, conversation, voiceChat]);

  // ── Mount: initialize based on starting phase ─────────────────────────────
  useEffect(() => {
    if (!conversationId || !user) return;

    const phase    = conversation?.currentPhase    || 'knowledge';
    const subphase = conversation?.currentSubphase || 'collection';

    if (phase === 'practice' && ROLEPLAY_SUBPHASES.has(subphase)) {
      hydrateRoleplaySession();
    } else {
      // Chat mode: build initial message list from conversationMessages prop
      const phaseStart = conversation?.phaseStartMessageOrder ?? null;
      const initMsgs = (conversationMessages || [])
        .filter(m =>
          subphase === 'scenario_creation' && phaseStart != null
            ? (m.messageOrder ?? 0) > phaseStart
            : true
        )
        .map(m => ({
          id: m.id || nextId(),
          type: (m.sender === 'scarlett' || m.role === 'ai') ? 'scarlett' : 'user',
          text: m.content || '',
        }));
      setChatMessages(initMsgs);
      setLoading(false);
    }
  }, [conversationId, user]); 

  // ── Handle session completion: re-fetch phase and switch to chat mode ─────
  const handleSessionComplete = useCallback(async () => {
    try {
      const data = await apiService.get(`/api/v1/conversations/${conversationId}/context`, user);
      const convData = data?.data?.conversation || (data?.data?.id ? data.data : null);
      if (convData) {
        const newPhase    = convData.currentPhase    || 'completed';
        const newSubphase = convData.currentSubphase || null;
        setCurrentPhase(newPhase);
        setCurrentSubphase(newSubphase);
        // Reset session control state
        setSessionId(null);
        setPinnedUtterance('');
        setPinnedMode('active');
        setPendingChar(null);
        if (newSubphase !== 'roleplay_debrief') {
          // In debrief, keep the roleplay history as the top of the conversation
          setMessages([]);
          const newMsgs = (convData.messages?.turns || convData.messages || []).map(m => ({
            id: m.id || nextId(),
            type: (m.sender === 'scarlett' || m.role === 'ai') ? 'scarlett' : 'user',
            text: m.content || '',
          }));
          setChatMessages(newMsgs);
        }
      }
    } catch (err) {
      console.error('[RoleplayView] handleSessionComplete error:', err);
      setCurrentPhase('completed');
      setCurrentSubphase(null);
      setSessionId(null);
    }
  }, [conversationId, user]);

  // ── Chat mode send handler ────────────────────────────────────────────────
  const handleChatSend = useCallback(async (text, { isVoice = false } = {}) => {
    if (!text?.trim() || isChatSending) return;

    setIsChatSending(true);
    if (inDebriefModeRef.current) {
      addMessages([{ id: nextId(), type: 'user', text: text.trim() }]);
    } else {
      setChatMessages(prev => [...prev, { id: nextId(), type: 'user', text: text.trim() }]);
    }

    try {
      const response = await apiService.post(
        `/api/v1/conversations/${conversationId}/messages`,
        { message: text.trim() },
        user
      );

      const data        = response?.data || response;
      const newPhase    = data?.currentPhase;
      const newSubphase = data?.currentSubphase;
      const aiText      = data?.aiResponse || data?.response || '';
      const freshCaseStudy = data?.conversationSummary;
      if (freshCaseStudy && Object.keys(freshCaseStudy).length > 0) {
        setCaseStudy(freshCaseStudy);
      } else if (inKnowledgeJustificationRef.current ||
          (newPhase === 'knowledge' && (newSubphase === 'justification' || newSubphase === 'justification_confirmation'))) {
        try {
          const ctxResp = await apiService.get(`/api/v1/conversations/${conversationId}/context`, user);
          const freshCs = ctxResp?.data?.caseStudy;
          if (freshCs && Object.keys(freshCs).length > 0) setCaseStudy(freshCs);
        } catch {
          // non-fatal
        }
      }

      if (aiText) {
        if (inDebriefModeRef.current) {
          addMessages([{ id: nextId(), type: 'scarlett', text: aiText }]);
        } else {
          setChatMessages(prev => [...prev, { id: nextId(), type: 'scarlett', text: aiText }]);
        }
        // On the scenario_creation -> engage transition, aiText is the coach's invisible
        // wrap-up (never rendered, since the view switches to roleplay mode this same tick).
        // The character's real opening line is spoken later, once loaded, by
        // hydrateRoleplaySession(aiText, isVoice) below — don't speak the coach text here.
        if (isVoice && voiceChat?.generateSpeech && newSubphase !== 'engage') {
          voiceChat.generateSpeech(aiText, 'coach');
        }
      }

      if (newPhase && newSubphase) {
        setCurrentPhase(newPhase);
        setCurrentSubphase(newSubphase);

        if (newSubphase === 'engage') {
          // Pre-seed the pinned panel before session hydration loads
          if (aiText) {
            setPinnedUtterance(aiText);
            setPendingChar(aiText);
          }
          // Kick off session loading (switching to roleplay mode). Only auto-speak
          // the character's real opening line (once hydration resolves it) if the
          // user was interacting by voice on this turn.
          hydrateRoleplaySession(aiText, isVoice);
        }
      }
    } catch (err) {
      console.error('RoleplayView chat send error:', err);
      const errMsg = { id: nextId(), type: 'scarlett', text: 'Something went wrong. Please try again.' };
      if (inDebriefModeRef.current) {
        addMessages([errMsg]);
      } else {
        setChatMessages(prev => [...prev, errMsg]);
      }
    } finally {
      setIsChatSending(false);
    }
  }, [isChatSending, conversationId, user, hydrateRoleplaySession, voiceChat, addMessages]);

  // ── Roleplay send handler ─────────────────────────────────────────────────
  const handleSend = useCallback(async (text, { isVoice = false } = {}) => {
    if (!text?.trim() || isSending || !sessionId) return;

    setIsSending(true);
    setPinnedMode('thinking');

    try {
      const resp = await apiService.post(
        `/api/v1/conversations/${conversationId}/messages`,
        { message: text.trim() },
        user
      );

      const data       = resp?.data || resp;
      const turn       = data?.roleplayTurn;
      const sessState  = data?.sessionState;
      const aiResponse = data?.aiResponse;

      if (!turn) {
        if (aiResponse) {
          addMessages([{ id: nextId(), type: 'scarlett', text: aiResponse }]);
          if (isVoice && voiceChat?.generateSpeech) voiceChat.generateSpeech(aiResponse, 'coach');
        }
        setPinnedMode('active');
        setIsSending(false);
        return;
      }

      const action = turn.coach_action;

      if (sessState?.phase_status) {
        setPhaseStatus(sessState.phase_status);
      }

      if (action === 'advance') {
        const isEnding = sessState?.status === 'debriefing' || sessState?.status === 'completed';
        const toInject = pendingCharUtterance || pinnedUtterance;
        // console.log('[RoleplayView] advance — toInject:', JSON.stringify(toInject));

        const promotedText = turn.promoted_response || text.trim();
        const promotedMsg  = { id: nextId(), type: 'client-promoted', text: promotedText };

        if (toInject) {
          addMessages([{ id: nextId(), type: 'character', text: toInject, characterName, characterRole }]);
          if (isVoice && voiceChat?.generateSpeech && turn.character_response) voiceChat.generateSpeech(turn.character_response, 'character');
          setTimeout(() => addMessages([promotedMsg]), 400);
        } else {
          addMessages([promotedMsg]);
        }

        if (toInject) {
          const nextCharUtterance = turn.character_response || '';
          setScriptExchanges((prev) => {
            // Remove any trailing character-only entry — it's now being completed by this promotion
            const base = prev.length > 0 && prev[prev.length - 1].clientText == null
              ? prev.slice(0, -1)
              : prev;
            const completed = [...base, { clientText: promotedText, characterText: toInject }];
            if (!nextCharUtterance) return completed;
            // Final turn: append character's closing line with no clientText (session is over)
            // Normal turn: mark pending with clientText: null so the next advance can replace it
            return isEnding
              ? [...completed, { characterText: nextCharUtterance }]
              : [...completed, { clientText: null, characterText: nextCharUtterance }];
          });
          setScriptOpen(true);
          setScriptActiveTab('gold');
        }

        if (turn.phase_achieved) {
          // When the session ends, current_phase stays 'lead' on the backend but we need
          // activePhase past index 3 so Lead renders as done, not active.
          const newPhase = isEnding
            ? phaseIndex('roleplay_debrief')
            : phaseIndex(sessState?.current_phase || 'engage');
          setActivePhase(newPhase);
          setPartialFill(0);
        } else {
          setPartialFill((p) => Math.min(0.7, p + 0.15));
        }

        if (isEnding && turn.character_response) {
          const closingMsgs = [{ id: nextId(), type: 'character', text: turn.character_response, characterName, characterRole }];
          if (turn.coaching_feedback) closingMsgs.push({ id: nextId(), type: 'scarlett', text: turn.coaching_feedback });
          setTimeout(() => addMessages(closingMsgs), 900);
        } else if (turn.coaching_feedback) {
          setTimeout(() => {
            addMessages([{ id: nextId(), type: 'scarlett', text: turn.coaching_feedback }]);
          }, 900);
        }

        if (isEnding) {
          setDebriefSessionId(sessionId);
          setTimeout(() => handleSessionComplete(), 1500);
          return;
        }

        setPinnedMode('thinking');
        setTimeout(() => {
          const nextUtterance = turn.character_response || '';
          setPinnedUtterance(nextUtterance);
          setPendingChar(nextUtterance);
          setPinnedMode('fading-in');
          setTimeout(() => setPinnedMode('active'), 700);
        }, 2000);

      } else if (action === 'redirect' || action === 'intercept') {
        const failedMsgs = [
          { id: nextId(), type: 'client-failed', text: text.trim() },
        ];
        if (turn.coaching_feedback) {
          failedMsgs.push({ id: nextId(), type: 'scarlett', text: turn.coaching_feedback });
        }
        addMessages(failedMsgs);
        if (isVoice && turn.coaching_feedback && voiceChat?.generateSpeech) voiceChat.generateSpeech(turn.coaching_feedback, 'coach');
        setPinnedMode('active');

      } else if (action === 'model') {
        const modelMsgs = [
          { id: nextId(), type: 'client-failed', text: text.trim() },
        ];
        if (turn.coaching_feedback) {
          modelMsgs.push({ id: nextId(), type: 'scarlett', text: turn.coaching_feedback });
        }
        if (turn.model_response) {
          const modelText = turn.model_explanation
            ? `${turn.model_response}\n\n${turn.model_explanation}`
            : turn.model_response;
          modelMsgs.push({ id: nextId(), type: 'scarlett', text: modelText, label: 'Model response' });
        }
        addMessages(modelMsgs);
        if (isVoice && voiceChat?.generateSpeech) {
          const spokenText = turn.model_response
            ? (turn.model_explanation ? `${turn.model_response}\n\n${turn.model_explanation}` : turn.model_response)
            : turn.coaching_feedback;
          if (spokenText) voiceChat.generateSpeech(spokenText, 'coach');
        }
        setPinnedMode('active');
      }
    } catch (err) {
      console.error('RoleplayView send error:', err);
      addMessages([{
        id: nextId(),
        type: 'scarlett',
        text: 'Something went wrong. Please try again.',
      }]);
      setPinnedMode('active');
    } finally {
      setIsSending(false);
    }
  }, [
    isSending, sessionId, conversationId, user,
    pendingCharUtterance, pinnedUtterance,
    characterName, characterRole,
    addMessages, handleSessionComplete, voiceChat,
  ]);

  const handleRequestScriptOpen = useCallback(() => setScriptOpen(true), []);

  // ── Early exit ────────────────────────────────────────────────────────────
  const handleExit = useCallback(async () => {
    if (!sessionId) return;
    try {
      await apiService.post(
        `/api/v1/roleplay/sessions/${sessionId}/end`,
        { conversation_id: conversationId },
        user
      );
    } catch (err) {
      console.error('RoleplayView exit error:', err);
    }
    await handleSessionComplete();
  }, [sessionId, conversationId, user, handleSessionComplete]);

  // ── Loading / error ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="roleplay-container">
        <div className="rp-loading">Preparing your coaching session…</div>
      </div>
    );
  }

  if (hydrationError) {
    return (
      <div className="roleplay-container">
        <div className="rp-loading rp-error">{hydrationError}</div>
      </div>
    );
  }

  // ── Chat mode render (knowledge / scenario_creation / implementation / post-roleplay) ─
  if (!inRoleplayMode) {
    return (
      <div className="roleplay-container">
        <RoleplayTopBar
          conversationTitle={conversation?.title}
          conversationId={conversationId}
          onTitleUpdate={onTitleUpdate}
          activePhase={activePhase}
          partialFill={partialFill}
          sessionNum={sessionNum}
          conversationType={conversation?.type}
          showProgress={true}
          inDebriefMode={inDebriefMode}
        />
        <RoleplayPrintDocument
          conversationTitle={conversation?.title}
          caseStudy={caseStudy}
          exchanges={scriptExchanges}
          characterName={characterName}
          debriefReport={debriefReport}
        />
        <div className="rp-main">
          <div className="rp-conversation-column">
            <div className="rp-scenario-creation-area">
              {chatMessages.map(msg => (
                <div
                  key={msg.id}
                  className={`rp-msg ${msg.type === 'scarlett' ? 'rp-msg-scarlett' : 'rp-msg-user'}`}
                >
                  <div className="rp-msg-label">
                    {msg.type === 'scarlett' ? 'Scarlett' : 'You'}
                  </div>
                  <p className="rp-msg-text">{msg.text}</p>
                </div>
              ))}
              <div ref={chatBottomRef} />
            </div>
            <RoleplayInputArea
              onSendMessage={handleChatSend}
              disabled={isChatSending}
            />
          </div>
          {inImplementationMode && (
            <ImplementationPanel
              caseStudy={caseStudy}
              exchanges={scriptExchanges}
              debriefReport={debriefReport}
              onRetryDebriefBody={retryDebriefBody}
              characterName={characterName}
              open={implementPanelOpen}
              onToggle={() => setImplementPanelOpen(o => !o)}
            />
          )}
          {inKnowledgeJustification && (
            <KnowledgeSituationPanel caseStudy={caseStudy} />
          )}
        </div>
      </div>
    );
  }

  // ── Roleplay mode render ──────────────────────────────────────────────────
  const showPinnedPanel = !inDebriefMode && (Boolean(pinnedUtterance) || pinnedMode === 'thinking');

  return (
    <div className="roleplay-container">
      <RoleplayTopBar
        conversationTitle={conversation?.title}
        conversationId={conversationId}
        onTitleUpdate={onTitleUpdate}
        activePhase={activePhase}
        phaseStatus={phaseStatus}
        partialFill={partialFill}
        sessionNum={sessionNum}
        conversationType={conversation?.type}
        showProgress={true}
        inDebriefMode={inDebriefMode}
      />
      <RoleplayPrintDocument
        conversationTitle={conversation?.title}
        caseStudy={caseStudy}
        exchanges={scriptExchanges}
        characterName={characterName}
        debriefReport={debriefReport}
      />

      <div className="rp-main">
        <div className="rp-conversation-column">
          <RoleplayConversationArea
            messages={messages}
            newMessageIds={newMessageIds}
            onExit={handleExit}
          />

          {showPinnedPanel && (
            <RoleplayPinnedPanel
              characterName={characterName}
              characterRole={characterRole}
              utterance={pinnedUtterance}
              mode={pinnedMode}
              onPlayUtterance={() => voiceChat?.generateSpeech(pinnedUtterance, 'character')}
              isPlayingUtterance={Boolean(voiceChat?.isSpeaking || voiceChat?.isProcessing)}
            />
          )}

          <RoleplayInputArea
            onSendMessage={inDebriefMode ? handleChatSend : handleSend}
            disabled={inDebriefMode ? isChatSending : isSending}
            placeholder={inputPlaceholder}
          />
        </div>

        <RoleplayScriptPanel
          exchanges={scriptExchanges}
          open={scriptOpen}
          onToggle={() => setScriptOpen((o) => !o)}
          characterName={characterName}
          caseStudy={caseStudy}
          debriefReport={debriefReport}
          onRetryDebriefBody={retryDebriefBody}
          debriefActive={currentSubphase === 'roleplay_debrief'}
          activeTab={scriptActiveTab}
          onTabChange={setScriptActiveTab}
          onRequestOpen={handleRequestScriptOpen}
        />
      </div>
    </div>
  );
};

RoleplayViewInner.propTypes = {
  conversationId: PropTypes.string.isRequired,
  conversation: PropTypes.object.isRequired,
  conversationMessages: PropTypes.array,
  onTitleUpdate: PropTypes.func.isRequired,
};

// Outer wrapper provides VoiceChatContext
const RoleplayView = (props) => (
  <VoiceChatProvider conversationId={props.conversationId}>
    <RoleplayViewInner {...props} />
  </VoiceChatProvider>
);

RoleplayView.propTypes = {
  conversationId: PropTypes.string.isRequired,
  conversation: PropTypes.object.isRequired,
  conversationMessages: PropTypes.array,
  onTitleUpdate: PropTypes.func.isRequired,
};

export default RoleplayView;
